import { useMemo } from "react";

import TableContextProvider from "components/Table/Context";
import { ColumnConfig } from "components/Table/types";
import useTableSort from "components/Table/useTableSort";
import Table from "ds/components/Table";
import { SearchQueryOrderDirection } from "types/generated";

import EnvironmentTableHeader from "./Header/EnvironmentTableHeader";

type EnvironmentTableProps<T extends { id: string }> = {
  columnsConfig: ColumnConfig;
  emptyTable?: JSX.Element;
  items: T[];
  rowRenderer: (item: T, key: string) => JSX.Element;
  rowHeaderId: string;
  tableId: string;
  endCustomColumns?: ColumnConfig | null;
};

const EnvironmentTable = <T extends { id: string }>({
  columnsConfig,
  emptyTable,
  items,
  rowRenderer,
  rowHeaderId,
  tableId,
  endCustomColumns,
}: EnvironmentTableProps<T>) => {
  const initialColumnView = useMemo(() => {
    return {
      visible: Object.keys(columnsConfig),
      hidden: [],
    };
  }, [columnsConfig]);

  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: tableId,
    sortConfig: {},
    initialSortOption: initialColumnView.visible[0],
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  return (
    <TableContextProvider
      columnsConfig={columnsConfig}
      endCustomColumns={endCustomColumns}
      initialColumnViewConfig={initialColumnView}
      rowHeaderId={rowHeaderId}
      localStorageId={tableId}
      setSortDescriptor={handleSortOptionChange}
      sortDescriptor={sortDescriptor}
    >
      {!!items.length && (
        <Table ariaLabel="Environment Variables">
          <EnvironmentTableHeader tableId={tableId} />
          {items.map((item) => {
            const key = `${tableId}-row-${item.id}`;
            return rowRenderer(item, key);
          })}
        </Table>
      )}
      {!items.length && emptyTable && emptyTable}
    </TableContextProvider>
  );
};

export default EnvironmentTable;
